const DISALLOWED_SCHEME_NAMES = ['javascript', 'data', 'vbscript', 'file'];

const APP_SCHEMES = ['cheqroom:', 'cheqroom-development:', 'cheqroom-release:', 'cheqroom-staging:', 'exp:'];

/** Production only ever hands off to the real production app; non-prod app schemes are only relevant for testing. */
function getAllowedAppSchemes(): string[] {
	return process.env.ENVIRONMENT === 'production' ? ['cheqroom:'] : APP_SCHEMES;
}

/** Scheme name only (e.g. "javascript"); tab/CR/LF are stripped so "java\tscript" → "javascript". */
function normalizedSchemePrefix(value: string): string {
	const colonIndex = value.indexOf(':');
	if (colonIndex === -1) return value;
	return value
		.slice(0, colonIndex)
		.replace(/[\t\r\n]+/g, '')
		.toLowerCase();
}

function getAllowlistedOrigins(): string[] {
	const origins: string[] = [];
	if (typeof window !== 'undefined') origins.push(window.location.origin);
	try {
		if (process.env.APP_URL) origins.push(new URL(process.env.APP_URL).origin);
	} catch {
		/* invalid APP_URL */
	}
	try {
		if (process.env.PORTAL_URL) origins.push(new URL(process.env.PORTAL_URL).origin);
	} catch {
		/* invalid PORTAL_URL */
	}
	try {
		if (process.env.LOGIN_URL) origins.push(new URL(process.env.LOGIN_URL).origin);
	} catch {
		/* invalid LOGIN_URL */
	}

	return origins;
}

/**
 * Validates untrusted redirect/navigate paths: allows relative paths or http(s) URLs
 * with allowlisted origin (current, APP_URL, PORTAL_URL). Rejects empty, dangerous
 * schemes (javascript:, data:, etc.), and non-allowlisted origins. Use before
 * setting window.location.href to mitigate XSS and open redirects.
 */
export type ValidateRedirectUri = (value: string | null | undefined) => string | undefined;

export const validateRedirectUri: ValidateRedirectUri = (value) => {
	const trimmed = typeof value === 'string' ? value.trim() : '';
	if (!trimmed) return undefined;

	const scheme = normalizedSchemePrefix(trimmed);
	if (DISALLOWED_SCHEME_NAMES.includes(scheme)) return undefined;

	// Relative path only (reject protocol-relative "//")
	if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed;

	try {
		const base = typeof window !== 'undefined' ? window.location.origin : process.env.APP_URL;
		const url = new URL(trimmed, base);

		if (getAllowedAppSchemes().includes(url.protocol)) return trimmed;

		if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
		const allowed = getAllowlistedOrigins();
		return allowed.length > 0 && allowed.includes(url.origin) ? trimmed : undefined;
	} catch {
		return undefined;
	}
};

/**
 * Allows same-origin relative paths and absolute http(s) URLs (any host).
 * Rejects empty values, protocol-relative URLs, and non-http(s) schemes
 * (javascript:, data:, vbscript:, file:, …). Use before window.open / embed src
 * for attachment or CDN links that are not on the app origin allowlist.
 */
export const validateHttpUrl = (value: string | null | undefined): string | undefined => {
	const trimmed = typeof value === 'string' ? value.trim() : '';
	if (!trimmed) return undefined;

	const scheme = normalizedSchemePrefix(trimmed);
	if (DISALLOWED_SCHEME_NAMES.includes(scheme)) return undefined;

	// Protocol-relative URLs can point anywhere; require an explicit scheme or a path.
	if (trimmed.startsWith('//')) return undefined;

	if (trimmed.startsWith('/')) return trimmed;

	try {
		const url = new URL(trimmed);
		if (url.protocol !== 'http:' && url.protocol !== 'https:') return undefined;
		return trimmed;
	} catch {
		return undefined;
	}
};

const DEFAULT_WINDOW_OPEN_FEATURES = 'noopener,noreferrer';

function openIfSafe(safe: string | undefined, target: string, features: string): Window | null {
	if (safe === undefined) return null;
	// Value validated by validateRedirectUri / validateHttpUrl (scheme + allowlist) above
	return window.open(safe, target, features); // nosemgrep
}

/**
 * window.open for in-app paths: same allowlist as validateRedirectUri
 * (relative paths or http(s) on APP_URL / PORTAL_URL / current origin).
 */
export const safeWindowOpen = (
	value: string,
	target = '_blank',
	features = DEFAULT_WINDOW_OPEN_FEATURES
): Window | null => openIfSafe(validateRedirectUri(value), target, features);

/**
 * window.open for http(s) URLs on any host (attachments, CDN). Rejects javascript: etc.
 */
export const safeWindowOpenHttp = (
	value: string,
	target = '_blank',
	features = DEFAULT_WINDOW_OPEN_FEATURES
): Window | null => openIfSafe(validateHttpUrl(value), target, features);

/**
 * Safe redirect: validates then assigns to window.location.href. Use this instead of
 * assigning to location.href directly so the allowlist + scheme checks are enforced.
 */
export const safeWindowLocationHref = (value: string): void => {
	const safe = validateRedirectUri(value);
	if (safe === undefined) return;
	// ensure our safe redirect is not our current path and query.
	// Use origin as base so relative paths (e.g. /equipment/123) resolve correctly.
	const redirectSafe = new URL(safe, window.location.origin);
	if (
		redirectSafe.origin === window.location.origin &&
		redirectSafe.pathname === window.location.pathname &&
		redirectSafe.search === window.location.search
	)
		return;
	// Value validated by validateRedirectUri (allowlist + scheme check) above; safe for location.href
	window.location.href = safe; // nosemgrep
};
